import React, { useRef, useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTheme } from '@mui/material/styles';
import './TabCustom.css';

/**
 * CustomTabs - pill/rounded style tab bar, scrollable with arrows (arrows outside, not overlapping), theme-aware
 * @param {Object[]} tabs - Array of {label, value}
 * @param {any} value - Currently selected value
 * @param {function} onChange - (event, value) => void
 * @param {object} sx - Optional sx for Box
 * @param {object} style - Optional style for Box
 */
const SCROLL_AMOUNT = 150;
const CustomTabs = ({ tabs, value, onChange, sx = {}, style = {} }) => {
    const theme = useTheme();
    const scrollRef = useRef();
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    // Check if scroll arrows are needed
    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowLeft(el.scrollLeft > 0);
        setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        checkScroll();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
            el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [tabs.length]);

    const handleScroll = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * SCROLL_AMOUNT, behavior: 'smooth' });
    };

    return (
        <div
            className="tab-nav"
            style={{
                ...style,
                background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default,
                color: theme.palette.text.primary,
            }}
        >
            {showLeft && (
                <IconButton
                    size="small"
                    onClick={() => handleScroll(-1)}
                    sx={{
                        mr: 1,
                        '&:hover': { background: theme.palette.action.hover },
                    }}
                >
                    <ArrowBackIosIcon fontSize="small" />
                </IconButton>
            )}
            <div
                ref={scrollRef}
                className="tab-container"
            >
                {tabs.map((tab, idx) => {
                    const selected = value === tab.value;
                    return (
                        <button
                            key={tab.value}
                            onClick={e => onChange(e, tab.value)}
                            className={`tab-button ${selected ? 'active' : ''}`}
                            style={{
                                color: selected ? (theme.palette.OrgCardColor?.main || theme.palette.primary.main) : theme.palette.text.primary,
                                background: selected
                                    ? (theme.palette.mode === 'dark' ? theme.palette.background.default : '#f8e9ffdc')
                                    : 'transparent',
                            }}
                        >
                            {tab.label}
                            {selected && <div className="tab-indicator" />}
                        </button>
                    );
                })}
            </div>
            {showRight && (
                <IconButton
                    size="small"
                    onClick={() => handleScroll(1)}
                    sx={{
                        ml: 1,
                        '&:hover': { background: theme.palette.action.hover },
                    }}
                >
                    <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
            )}
        </div>
    );
};

export default CustomTabs;